import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, TrendingUp, Calendar, Award, Zap, DollarSign, Building2 } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import ProgressBar from '../components/ProgressBar';
import AnimatedCounter from '../components/AnimatedCounter';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { yearlyTotals, tamData, objectifs, TAM_TOTAL, objectifsY1 } from '../mock';

export default function VisionPage() {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  
  const total5Years = yearlyTotals.reduce((acc, year) => ({
    revenus: acc.revenus + year.revenus,
    baiia: acc.baiia + year.baiia,
    gmv: acc.gmv + year.gmvRegulier + year.gmvSezzle
  }), { revenus: 0, baiia: 0, gmv: 0 });

  const publicTotal = objectifs.total - objectifs.prive;

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/vision" />
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
              {language === 'fr' ? 'Vision & Objectifs' : 'Vision & Objectives'}
            </CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Vision statement */}
            <div className="p-6 bg-gradient-to-br from-lime-500/10 to-transparent rounded-xl border border-lime-500/30">
              <h3 className="text-2xl font-bold text-lime-400 mb-4">{language === 'fr' ? 'Notre Vision' : 'Our Vision'}</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                {language === 'fr' 
                  ? <>Transformer durablement l'alimentation scolaire et faire d'EcoLunch la <span className="text-lime-400 font-semibold">norme numérique officielle</span> dans les 10 provinces canadiennes.</>
                  : <>Sustainably transform school meals and make EcoLunch the <span className="text-lime-400 font-semibold">official digital standard</span> across 10 Canadian provinces.</>}
              </p>
            </div>

            {/* OBJECTIFS Y1 — 2 SCÉNARIOS */}
            <div className="p-6 bg-gradient-to-br from-cyan-500/15 via-lime-500/10 to-transparent rounded-2xl border-2 border-cyan-500/40" data-testid="vision-y1-objectives">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-cyan-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                  {language === 'fr' ? 'Année 1 — Pré-seed' : 'Year 1 — Pre-seed'}
                </span>
                <span className="text-gray-300 text-sm">{language === 'fr' ? 'Sept 2026 → Août 2027' : 'Sep 2026 → Aug 2027'}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                {language === 'fr' ? 'Objectifs Année 1 — Minimum vs Réaliste' : 'Year 1 Objectives — Minimum vs Realistic'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Minimum scenario */}
                <Card className="bg-black/60 border-cyan-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-cyan-400 text-base">{language === 'fr' ? 'Scénario Minimum' : 'Minimum Scenario'}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                      <p className="text-xs text-gray-400 uppercase mb-1">{language === 'fr' ? 'M6 — Février' : 'M6 — February'}</p>
                      <p className="text-xl font-bold text-cyan-400">{objectifsY1.minimum.m6.ecoles} {language === 'fr' ? 'écoles' : 'schools'} + {objectifsY1.minimum.m6.garderies} {language === 'fr' ? 'garderies' : 'daycares'}</p>
                      <p className="text-xs text-gray-500">{objectifsY1.minimum.m6.repasJour.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')} {language === 'fr' ? 'repas/jour' : 'meals/day'} • {(objectifsY1.minimum.m6.gmvAnnualise / 1000000).toFixed(1)}M $ GMV</p>
                    </div>
                    <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                      <p className="text-xs text-gray-400 uppercase mb-1">{language === 'fr' ? 'M12 — Août' : 'M12 — August'}</p>
                      <p className="text-xl font-bold text-cyan-400">{objectifsY1.minimum.m12.ecoles.toLocaleString()} + {objectifsY1.minimum.m12.garderies.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">{objectifsY1.minimum.m12.repasJour.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')} {language === 'fr' ? 'repas/jour' : 'meals/day'} • {(objectifsY1.minimum.m12.gmvAnnualise / 1000000).toFixed(1)}M $ GMV</p>
                    </div>
                  </CardContent>
                </Card>
                {/* Realiste scenario */}
                <Card className="bg-black/60 border-lime-500/40 shadow-[0_0_20px_rgba(132,204,22,0.2)]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lime-400 text-base flex items-center gap-2">
                      {language === 'fr' ? 'Scénario Réaliste' : 'Realistic Scenario'}
                      <span className="px-2 py-0.5 bg-lime-500/30 text-lime-300 text-[9px] rounded-full uppercase">{language === 'fr' ? 'Cible' : 'Target'}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 bg-lime-500/10 rounded-lg border border-lime-500/30">
                      <p className="text-xs text-gray-400 uppercase mb-1">{language === 'fr' ? 'M6 — Février' : 'M6 — February'}</p>
                      <p className="text-xl font-bold text-lime-400">{objectifsY1.realiste.m6.ecoles} {language === 'fr' ? 'écoles' : 'schools'} + {objectifsY1.realiste.m6.garderies} {language === 'fr' ? 'garderies' : 'daycares'}</p>
                      <p className="text-xs text-gray-500">{objectifsY1.realiste.m6.repasJour.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')} {language === 'fr' ? 'repas/jour' : 'meals/day'} • {(objectifsY1.realiste.m6.gmvAnnualise / 1000000).toFixed(1)}M $ GMV</p>
                    </div>
                    <div className="p-3 bg-lime-500/10 rounded-lg border border-lime-500/30">
                      <p className="text-xs text-gray-400 uppercase mb-1">{language === 'fr' ? 'M12 — Août' : 'M12 — August'}</p>
                      <p className="text-xl font-bold text-lime-400">{objectifsY1.realiste.m12.ecoles.toLocaleString()} + {objectifsY1.realiste.m12.garderies.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">{objectifsY1.realiste.m12.repasJour.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')} {language === 'fr' ? 'repas/jour' : 'meals/day'} • {(objectifsY1.realiste.m12.gmvAnnualise / 1000000).toFixed(1)}M $ GMV</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <p className="text-xs text-gray-400 mt-4 border-t border-cyan-500/20 pt-3">
                {language === 'fr'
                  ? 'Note : Les prévisions s\'arrêtent volontairement à l\'Année 1. À partir de février, EcoLunch lance une ronde seed. Au-delà dépend du capital obtenu et de la traction réelle.'
                  : 'Note: Forecasts intentionally stop at Year 1. Starting February, EcoLunch launches a seed round. Beyond depends on capital raised and actual traction.'}
              </p>
            </div>

            {/* Mission */}
            <div className="p-6 bg-black/40 rounded-xl border border-cyan-500/30">
              <h3 className="text-xl font-bold text-cyan-400 mb-4">Mission</h3>
              <div className="space-y-3">
                <p className="text-gray-300 flex items-start gap-2">
                  <span className="text-lime-500 mt-1">&#8226;</span>
                  <span>{language === 'fr' ? 'Simplifier la vie des parents' : 'Simplify parents\' lives'}</span>
                </p>
                <p className="text-gray-300 flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">&#8226;</span>
                  <span>{language === 'fr' ? 'Offrir le contrôle aux écoles et l\'accès aux données centralisées au CSS' : 'Give schools control and centralized data access to school boards'}</span>
                </p>
                <p className="text-gray-300 flex items-start gap-2">
                  <span className="text-orange-500 mt-1">&#8226;</span>
                  <span>{language === 'fr' ? 'Assurer la qualité et la sécurité alimentaire pour tous les enfants' : 'Ensure food quality and safety for all children'}</span>
                </p>
              </div>
            </div>


            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
